import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { getUnitLabel, supportsHandlingUnits, toDisplayQty, type BundleConfig } from '../lib/quantities';
import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';
import type { ConfirmArrivalUnitMode } from '../model/types';
import { ConfirmArrivalInventoryQty } from './confirm-arrival-inventory-qty';

interface ConfirmArrivalQuantityFigureProps {
  line: BundleConfig;
  unitMode: ConfirmArrivalUnitMode;
  /** Inventory units. */
  quantity: number;
  /**
   * `total` — what the line holds (Expected / In Stock), with the bundle it
   * comes in; `remaining` — muted, and red once the entry overshoots.
   */
  kind: 'total' | 'remaining';
}

/** A read-only quantity column, in the table's unit, with the inventory echo in handling mode. */
export function ConfirmArrivalQuantityFigure({ line, unitMode, quantity, kind }: ConfirmArrivalQuantityFigureProps) {
  const { t } = useTranslation();
  const hasHandlingUnits = supportsHandlingUnits(line);
  const isHandlingMode = unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING;

  return (
    <span className="flex flex-col text-sm tabular-nums">
      <span
        className={cn(
          kind === 'remaining' && (quantity < 0 ? 'font-medium text-destructive' : 'text-muted-foreground')
        )}>
        {toDisplayQty(line, unitMode, quantity)} {getUnitLabel(line, unitMode)}
      </span>
      {kind === 'total' && hasHandlingUnits && !isHandlingMode && (
        <span className="text-xs text-muted-foreground">
          {t('receive.confirm.inBundle', 'in {{bundle}}', { bundle: line.bundleTypeDescription ?? '' })}
        </span>
      )}
      {isHandlingMode && hasHandlingUnits && (
        <ConfirmArrivalInventoryQty quantity={quantity} unitLabel={line.ltuTypeDescription} />
      )}
    </span>
  );
}
